import type React from "react"
import { useEffect, useRef } from "react"
import { Card } from "react-bootstrap"
import * as echarts from "echarts"

// @ts-ignore
const RegionWiseChart: React.FC = ({ BarChartData }) => {
    const chartRef = useRef<HTMLDivElement>(null)

    const providers = BarChartData?.xAxis?.providers
    const regions = BarChartData?.xAxis?.regions

    // Repeat region name 3 times (Gemini, OpenAI, OSS)
    const regionLabels = regions && regions?.length > 0 && regions.flatMap((region: any) =>
        Array(providers.length).fill(region)
    )

    const providerLabels = regions?.flatMap(() => providers) || []

    useEffect(() => {
        if (!chartRef.current) return

        const chart = echarts.init(chartRef.current)

        const option = {
            title: {
                text: BarChartData?.title,
                textStyle: {
                    fontSize: 20,
                    fontWeight: 400,
                    color: "#18181B",
                    lineHeight: 16,
                },
                left: 0,
                top: 0,
                subtext: BarChartData?.subtitle,
                subtextStyle: {
                    fontSize: 14,
                    color: "#838FB4",
                    fontWeight: 400,
                    lineHeight: 16,
                    width: 700,
                    overflow: "break",
                },
            },
            tooltip: {
                trigger: "axis",
                formatter: function (params: any) {
                    const index = params[0].dataIndex
                    const provider = providers[index % providers.length]
                    const region = regions[Math.floor(index / providers.length)]

                    let tooltipText = `
                        <strong>Region:</strong> ${region}<br/>
                        <strong>Provider:</strong> ${provider}<br/><br/>
                    `

                    params.forEach((item: any) => {
                        tooltipText += `
                            ${item.marker}
                            ${item.seriesName}: ${item.value}<br/>
                        `
                    })

                    return tooltipText
                }
            },

            legend: {
                data: BarChartData?.series.map((s: any) => s.name),
                bottom: 0,
                itemGap: 20,
                icon: "circle",
            },
            grid: {
                top: "25%",
            },
            xAxis: [
                // Providers (lowest)
                {
                    type: "category",
                    // data: providers?.flatMap(() => providers),
                    data: providerLabels,
                    axisLabel: {
                        rotate: 45,
                        fontSize: 11,
                        color: "#4B4B53",
                        interval: 0,
                        formatter: function (value: string) {
                            const maxLength = 10;
                            return value.length > maxLength ? value.substring(0, maxLength) + '...' : value;
                        }
                    },
                    axisTick: {
                        alignWithLabel: true,
                    }
                },

                // Regions (just above providers, still at bottom)
                {
                    type: "category",
                    data: regionLabels,
                    position: "bottom",
                    offset: 42, //  pushes region labels below providers
                    axisLabel: {
                        // rotate: 45,
                        fontSize: 11,
                        color: "#18181B",
                        fontWeight: 500,
                        interval: function (index: number) {
                            // Show region name only once per group (centered)
                            return index % providers.length === Math.floor(providers.length / 2)
                        }
                    },
                    axisTick: {
                        show: false,
                    },
                    axisLine: {
                        show: false,
                    }
                }
            ],

            yAxis: {
                type: "value",
                name: BarChartData?.yAxis?.name,
                nameLocation: "middle",
                // nameGap: 50,
                nameRotate: 90,
                nameTextStyle: {
                    color: "#18181B",
                    fontSize: 14,
                    fontWeight: 400,
                },
                axisLabel: {
                    fontSize: 12,
                    color: "#4B4B53",
                    fontWeight: 400,
                },
                splitLine: {
                    lineStyle: {
                        type: "dashed",
                        color: "rgba(0, 102, 204, 0.25)"
                    },
                },
                max: 8,
            },
            series: BarChartData?.series.map((seriesItem: any, index: any) => ({
                name: seriesItem?.name,
                type: "bar",
                stack: "total",
                data: seriesItem?.data,
                barMaxWidth: 30,
                itemStyle: {
                    color: index === 0 ? "#00B386" : "#EB5757",
                },
            })),
        }

        chart.setOption(option)

        const handleResize = () => {
            chart.resize()
        }

        window.addEventListener("resize", handleResize)

        return () => {
            window.removeEventListener("resize", handleResize)
            chart.dispose()
        }
        //eslint-disable-next-line
    }, [BarChartData])

    return (
        <Card className="h-100 shadow-sm">
            <Card.Body className="p-3">
                <div ref={chartRef} style={{ width: "100%", height: "450px" }} />
            </Card.Body>
        </Card>
    )
}

export default RegionWiseChart
